import React, { useEffect, useRef, useState } from "react"
import { HtmlSectionBody } from "@/components/HtmlSectionBody"
import { PreviewPlaceholder } from "@/components/PreviewPlaceholder"
import { PreviewNotice } from "@/components/PreviewNotice"
import { Link2, Check } from "lucide-react"
import type { DocumentSection } from "@/lib/types"

interface CollapsibleSectionProps {
  section: DocumentSection
  // Last readable section of a preview: fade out and show the notice.
  previewCutoff?: boolean
}

export function CollapsibleSection({
  section,
  previewCutoff = false,
}: CollapsibleSectionProps) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [stickyTop, setStickyTop] = useState(0)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const heading = headingRef.current
    if (!heading) return
    const scrollParent = heading.closest(".overflow-auto") ?? window
    const update = () => {
      const titleEl = heading.closest(".min-w-0, .flex-1")?.querySelector("[data-sticky-title]") as HTMLElement | null
      setStickyTop(titleEl ? titleEl.offsetHeight : 0)
    }
    update()
    const target = scrollParent === window ? document : scrollParent
    target.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      target.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [])

  return (
    <div id={`section-${section.section_key}`} className="scroll-mt-24">
      <h3
        ref={headingRef}
        className="group/heading sticky z-[5] text-xl font-semibold text-black dark:text-white bg-background py-2 -mx-1 px-1"
        style={{ top: stickyTop }}
      >
        <span className="flex items-center gap-1.5">
          {section.heading}
          <button
            type="button"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            title="Copy link to section"
            onClick={() => {
              const url = new URL(window.location.href)
              url.hash = `section-${section.section_key}`
              navigator.clipboard.writeText(url.toString())
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-600" />
                <span className="text-xs font-normal text-green-600">Copied!</span>
              </>
            ) : (
              <Link2 className="h-4 w-4" />
            )}
          </button>
        </span>
        <hr className="mt-2 border-t border-border" />
      </h3>
      <div className="relative rounded-md bg-white px-4 pb-[0.5em] pt-[0.5em] dark:bg-card">
        {section.preview_placeholder ? (
          <PreviewPlaceholder blocks={section.preview_placeholder} />
        ) : (
          <HtmlSectionBody html={section.body_html} />
        )}
        {previewCutoff && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-md bg-gradient-to-b from-transparent to-white dark:to-card"
          />
        )}
      </div>
      {previewCutoff && <PreviewNotice />}
    </div>
  )
}
